import {
  Component,
  input,
  output,
  signal,
  ViewChild,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import {
  EXTENSIONES_ACEPTADAS_INFORME,
  EXTENSIONES_PERMITIDAS_INFORME,
  MAX_FILE_SIZE_INFORME,
  esTipoArchivoInformePermitido
} from 'src/app/domain/model/cumplimiento/archivo-informe.constants';
import { DropZoneDirective } from 'src/app/presentation/shared/file-uploader/drop-zone.directive';
import { MAX_OBSERVACIONES_ENVIO_INFORME } from 'src/app/common/constants-ejb';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/**
 * Datos del archivo procesado para enviar al backend.
 * Reutiliza la estructura de EnvioInformeRequest.
 */
export interface ArchivoInforme {
  /** Contenido del archivo en Base64 */
  archivoContenido: string;
  /** Nombre del archivo con extensión */
  archivoNombre: string;
  /** Content-Type del archivo */
  archivoContentType: string;
}

/**
 * DUMB COMPONENT: SeccionEnviarInforme
 *
 * Panel "Enviar Informe" para cumplimientos de tipo INFORME.
 *
 * Legacy: EnviarInformeViewImpl.java + EnviarInformeViewImpl.ui.xml
 *
 * Campos:
 * - Archivo * (obligatorio) - File upload
 * - Observaciones (textarea, 200 chars max)
 * - Tipo de Informe * (obligatorio) - Parcial / Final
 * - Mail cuando aprueben informe (checkbox)
 *
 * @example
 * <app-seccion-enviar-informe
 *   [formGroup]="form"
 *   (archivoSeleccionado)="onArchivoSeleccionado($event)">
 * </app-seccion-enviar-informe>
 */
@Component({
  selector: 'app-seccion-enviar-informe',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FieldsetModule,
    RadioButtonModule,
    CheckboxModule,
    InputTextarea,
    ButtonModule,
    TooltipModule,
    DropZoneDirective
  ],
  template: `
    <p-fieldset legend="Enviar Informe" [toggleable]="true" styleClass="mb-4">
      <div class="space-y-4" [formGroup]="formGroup()">
        <!-- Archivo * (obligatorio) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Archivo <span class="text-red-500">*</span>
          </label>

          <!-- Input file oculto -->
          <input
            #fileInput
            type="file"
            class="hidden"
            [accept]="extensionesAceptadas"
            (change)="onArchivoChange($event)"
          />

          @if (!archivoNombre()) {
            <!-- Zona de selección: clic o arrastrar y soltar (legacy: gwtupload MultiUploader) -->
            <div
              appDropZone
              [dropZoneDisabled]="cargandoArchivo()"
              (archivoSoltado)="procesarArchivo($event)"
              class="flex flex-wrap items-center gap-2 p-2 w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
            >
              <button
                pButton
                type="button"
                label="Seleccionar archivo"
                icon="pi pi-upload"
                class="p-button-outlined min-h-[44px]"
                [disabled]="cargandoArchivo()"
                (click)="fileInput.click()"
              ></button>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                o arrastre el archivo aquí
              </span>
            </div>
            <div class="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Formatos: {{ extensionesPermitidas }} | Máximo: 10 MB
            </div>
          } @else {
            <!-- Preview del archivo seleccionado -->
            <div
              class="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded"
            >
              <i class="pi pi-file text-blue-500 text-xl"></i>
              <div class="flex-1">
                <div class="font-medium text-blue-800">
                  {{ archivoNombre() }}
                </div>
                <div class="text-sm text-blue-600">
                  {{ formatearTamano(archivoTamano()) }}
                </div>
              </div>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-rounded p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Eliminar archivo"
                (click)="limpiarArchivo()"
              ></button>
            </div>
          }

          @if (mensajeErrorArchivo()) {
            <small class="text-red-500 mt-1 block">{{
              mensajeErrorArchivo()
            }}</small>
          }
        </div>

        <!-- Observaciones (legacy: taOrdenServicio) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Observaciones</label
          >
          <textarea
            pInputTextarea
            formControlName="observacionesInforme"
            (input)="sanitizeObservacionesInforme()"
            rows="4"
            [appContadorCaracteres]="maxObservacionesEnvio"
            class="w-full"
          >
          </textarea>
        </div>

        <!-- Tipo de Informe * (legacy: rbgTipoInforme) -->
        @if (!ocultarTipoInforme()) {
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Informe <span class="text-red-500">*</span>
            </label>
            <div class="flex items-center gap-6">
              <div class="flex items-center gap-2">
                <p-radioButton
                  formControlName="tipoInforme"
                  value="P"
                  inputId="tipoParcial"
                >
                </p-radioButton>
                <label for="tipoParcial" class="cursor-pointer">Parcial</label>
              </div>
              <div class="flex items-center gap-2">
                <p-radioButton
                  formControlName="tipoInforme"
                  value="F"
                  inputId="tipoFinal"
                >
                </p-radioButton>
                <label for="tipoFinal" class="cursor-pointer">Final</label>
              </div>
            </div>
          </div>
        }

        <!-- Mail cuando aprueben informe (legacy: scbMailInforme) -->
        @if (!ocultarTipoInforme()) {
          <div>
            <div class="flex items-center gap-2">
              <p-checkbox
                formControlName="snmailAprobacion"
                [binary]="true"
                inputId="mailAprobacion"
              >
              </p-checkbox>
              <label
                for="mailAprobacion"
                class="cursor-pointer text-sm text-gray-700"
              >
                Mail cuando aprueben informe
              </label>
            </div>
          </div>
        }
      </div>
    </p-fieldset>
  `
})
export class SeccionEnviarInformeComponent {
  readonly maxObservacionesEnvio = MAX_OBSERVACIONES_ENVIO_INFORME;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Inputs
  formGroup = input.required<FormGroup>();
  ocultarTipoInforme = input<boolean>(false);

  // Outputs
  archivoSeleccionado = output<ArchivoInforme | null>();

  // Estado interno
  archivoNombre = signal<string>('');
  archivoTamano = signal<number>(0);
  archivoContentType = signal<string>('');
  archivoBase64 = signal<string>('');
  mensajeErrorArchivo = signal<string>('');
  cargandoArchivo = signal<boolean>(false);

  // Constantes para el template
  readonly extensionesAceptadas = EXTENSIONES_ACEPTADAS_INFORME;
  readonly extensionesPermitidas = EXTENSIONES_PERMITIDAS_INFORME;

  /**
   * Maneja la selección de archivo desde el input.
   */
  async onArchivoChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.procesarArchivo(input.files?.[0]);
  }

  /**
   * Valida tipo/tamaño y convierte a Base64. Lo usan el clic y el arrastre.
   * Legacy: validarTipoArchivo() + validarTamanoArchivo() + fileToBase64()
   */
  async procesarArchivo(file: File | undefined): Promise<void> {
    this.mensajeErrorArchivo.set('');

    if (!file) {
      this.limpiarArchivo();
      return;
    }

    // Validar tipo de archivo
    if (!esTipoArchivoInformePermitido(file)) {
      this.mensajeErrorArchivo.set(
        `Formato no permitido. Use: ${EXTENSIONES_PERMITIDAS_INFORME}`
      );
      this.limpiarArchivo();
      return;
    }

    // Validar tamaño
    if (file.size > MAX_FILE_SIZE_INFORME) {
      this.mensajeErrorArchivo.set(
        'El archivo excede el tamaño máximo de 10 MB'
      );
      this.limpiarArchivo();
      return;
    }

    // Convertir a Base64
    this.cargandoArchivo.set(true);
    try {
      const base64 = await this.fileToBase64(file);

      this.archivoNombre.set(file.name);
      this.archivoTamano.set(file.size);
      this.archivoContentType.set(file.type);
      this.archivoBase64.set(base64);

      // Emitir archivo procesado
      const archivoInfo: ArchivoInforme = {
        archivoContenido: base64,
        archivoNombre: file.name,
        archivoContentType: file.type
      };
      this.archivoSeleccionado.emit(archivoInfo);

      // Actualizar form con nombre de archivo (para validación)
      this.formGroup().patchValue({ archivoInforme: file.name });
    } catch (error) {
      this.mensajeErrorArchivo.set('Error al procesar el archivo');
      this.limpiarArchivo();
    } finally {
      this.cargandoArchivo.set(false);
    }
  }

  /**
   * Convierte un File a string Base64.
   * Reutiliza lógica de EnviarInformeDialogComponent.
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remover el prefijo "data:application/pdf;base64,"
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Limpia el archivo seleccionado.
   */
  limpiarArchivo(): void {
    this.archivoNombre.set('');
    this.archivoTamano.set(0);
    this.archivoContentType.set('');
    this.archivoBase64.set('');
    this.archivoSeleccionado.emit(null);

    // Limpiar input file
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    // Limpiar form
    this.formGroup().patchValue({ archivoInforme: null });
  }

  /**
   * Formatea el tamaño del archivo para mostrar.
   */
  formatearTamano(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Obtiene los datos del archivo procesado para el request.
   * Usado por el formulario padre al guardar.
   */
  getArchivoInforme(): ArchivoInforme | null {
    if (!this.archivoBase64()) return null;

    return {
      archivoContenido: this.archivoBase64(),
      archivoNombre: this.archivoNombre(),
      archivoContentType: this.archivoContentType()
    };
  }

  /**
   * Verifica si hay un archivo válido seleccionado.
   */
  tieneArchivo(): boolean {
    return !!this.archivoBase64();
  }

  /**
   * Sanitiza el campo Observaciones contra XSS/SQLi (seguridad-girop §3).
   */
  sanitizeObservacionesInforme(): void {
    const ctrl = this.formGroup().get('observacionesInforme');
    if (ctrl) {
      ctrl.setValue(sanitizeTextarea(ctrl.value ?? ''), { emitEvent: false });
    }
  }
}
